/**
 * ptbfRollingsApi — RTK Query slice cho /purchase/ptbf-rollings/*.
 * Tag type: 'PTBFRolling'.
 *
 * Sprint 3 — endpoints:
 *   GET    /purchase/ptbf-rollings/list?contract_no=&page=&page_size=
 *   POST   /purchase/ptbf-rollings/create
 *   GET    /purchase/ptbf-rollings/{rolling_id}
 *   PUT    /purchase/ptbf-rollings/{rolling_id}
 *   DELETE /purchase/ptbf-rollings/{rolling_id}
 *   GET    /purchase/ptbf-rollings/{rolling_id}/print
 *   GET    /purchase/ptbf-rollings/{rolling_id}/print/cancel
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  PageResponse,
  PTBFRollingCreateIn,
  PTBFRollingListQuery,
  PTBFRollingOut,
  PTBFRollingUpdateIn,
} from '@/types/api'

export type PTBFRollingPrintKind = 'main' | 'cancel'

export const ptbfRollingsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPTBFRollings: b.query<
      PageResponse<PTBFRollingOut>,
      PTBFRollingListQuery | void
    >({
      query: (params) => ({
        url: '/purchase/ptbf-rollings/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo =
          (arg && (arg as PTBFRollingListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({
                type: 'PTBFRolling' as const,
                id: it.id,
              })),
              { type: 'PTBFRolling' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'PTBFRolling' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'PTBFRolling' as const, id: 'LIST' }]
      },
    }),
    getPTBFRolling: b.query<DataResponse<PTBFRollingOut>, number>({
      query: (id) => `/purchase/ptbf-rollings/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PTBFRolling', id }],
    }),
    createPTBFRolling: b.mutation<
      DataResponse<PTBFRollingOut>,
      PTBFRollingCreateIn
    >({
      query: (body) => ({
        url: '/purchase/ptbf-rollings/create',
        method: 'POST',
        body,
      }),
      // Save Roll cập nhật HĐ Purchase (local_p): futures_month + ptbf_diffs mới
      // + auto-tạo annex. Phải invalidate list HĐ (LocalP + ContractAll) VÀ
      // detail (LocalPDetail) để UI không hiện Futures Month/Diffs cũ.
      invalidatesTags: (res, _e, arg) => {
        const cn = res?.data?.contract_no ?? arg.contract_no
        return [
          { type: 'PTBFRolling', id: 'LIST' },
          { type: 'LocalP', id: 'LIST' },
          { type: 'LocalP', id: cn },
          { type: 'LocalPDetail', id: cn },
          { type: 'ContractAll', id: 'LIST' },
        ]
      },
    }),
    updatePTBFRolling: b.mutation<
      DataResponse<PTBFRollingOut>,
      { id: number; body: PTBFRollingUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/ptbf-rollings/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (res, _e, arg) => {
        const cn = res?.data?.contract_no
        return [
          { type: 'PTBFRolling', id: arg.id },
          { type: 'PTBFRolling', id: 'LIST' },
          { type: 'LocalP', id: 'LIST' },
          { type: 'ContractAll', id: 'LIST' },
          ...(cn
            ? [
                { type: 'LocalP' as const, id: cn },
                { type: 'LocalPDetail' as const, id: cn },
              ]
            : []),
        ]
      },
    }),
    deletePTBFRolling: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/ptbf-rollings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'PTBFRolling', id },
        { type: 'PTBFRolling', id: 'LIST' },
      ],
    }),
    /** Print PDF (placeholder Sprint 6). `kind` = 'main' | 'cancel'. */
    printPTBFRolling: b.query<
      DataResponse<LocalPPrintResult>,
      { id: number; kind: PTBFRollingPrintKind }
    >({
      query: ({ id, kind }) => {
        if (kind === 'main') return `/purchase/ptbf-rollings/${id}/print`
        return `/purchase/ptbf-rollings/${id}/print/${kind}`
      },
    }),
  }),
})

export const {
  useListPTBFRollingsQuery,
  useGetPTBFRollingQuery,
  useCreatePTBFRollingMutation,
  useUpdatePTBFRollingMutation,
  useDeletePTBFRollingMutation,
  useLazyPrintPTBFRollingQuery,
} = ptbfRollingsApi
